import { stumbling } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  type Filters,
  appendSimpleLabelsFilters,
  getFilterTupleOperand,
  isDateTimeTableFilter,
  isLabelsTableFilter,
} from '@ipfabric/utils';
import { tableDateFormat } from '@ipfabric/utils/date';
import { getDateForFilter } from 'components/ApiTable/helpers/getDateForFilter';
import {
  type ApiTableCommandFactory,
  advancedFilterInitialState,
} from 'components/ApiTable/model/ApiTableModel';
import { setFilters } from 'components/ApiTable/model/commands/setFilters';
import { unformatFilterValue } from 'components/Table/components/SimpleFilter/helpers/formatFilterValue';
import { getChildFilterField } from 'components/Table/components/SimpleFilter/helpers/getChildFilterField';
import { getFilterFromReadable } from 'components/Table/components/SimpleFilter/helpers/getFilterFromReadable';
import { addDays, format, isMatch } from 'date-fns';
import { reloadTableData } from './reloadTableData';
import { selectFilterPreset } from './selectFilterPreset';

export const submitSimpleFilters: ApiTableCommandFactory =
  (cmdContext, dispatch) => async () => {
    const {
      simpleFilterFields,
      intentFilters,
      endpoint,
      ruleContext,
      selectedFilterPreset,
    } = cmdContext.getState();

    if (Object.values(simpleFilterFields).some(({ error }) => error)) {
      return;
    }

    // Clear selected preset when submitting simple filters
    if (selectedFilterPreset) {
      await dispatch(selectFilterPreset, {
        preset: null,
      });
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
    const filters = endpoint.columns.reduce<Filters>((acc, { key }) => {
      const intentFilter = intentFilters[key];

      if (intentFilter) {
        (acc as any)[key] = ['color', 'eq', intentFilter];

        return acc;
      }

      const filterField = simpleFilterFields[key];

      if (!filterField) {
        return acc;
      }

      if (filterField.value === 'null') {
        (acc as any)[key] = ['empty', true];

        return acc;
      }

      const realFilterField = getChildFilterField(filterField) ?? filterField;

      if (isLabelsTableFilter(realFilterField.filter)) {
        if (
          typeof realFilterField.value !== 'string' ||
          realFilterField.value.trim() === ''
        ) {
          return acc;
        }

        appendSimpleLabelsFilters(acc, key, realFilterField.value);
        return acc;
      }

      if (Array.isArray(realFilterField.value)) {
        // array value is now only for multiselect filter
        if (realFilterField.value.length > 0) {
          acc.and = acc.and || [];
          acc.and.push({
            or: realFilterField.value.map((value) => ({
              [key]: ['eq', unformatFilterValue(realFilterField, value)],
            })),
          });
        }
        return acc;
      }

      const readableFilter = getFilterFromReadable(
        realFilterField.filter,
        realFilterField.value,
      );
      const readableFilterValue =
        readableFilter && getFilterTupleOperand(readableFilter, 1);
      const unformattedFilterValue =
        readableFilterValue !== undefined &&
        unformatFilterValue(realFilterField, readableFilterValue);
      const unformattedReadableFilter = readableFilter &&
        readableFilterValue !== undefined && [
          readableFilter[0],
          unformattedFilterValue,
        ];

      if (!unformattedReadableFilter) {
        return acc;
      }

      if (
        isDateTimeTableFilter(filterField.filter) &&
        readableFilterValue &&
        typeof readableFilterValue === 'string'
      ) {
        const getDatePart = () => {
          const operator = readableFilter[0];
          const dateString = readableFilterValue;

          // When the input date string uses the short 'yyyy-MM-dd' format (i.e., it contains no time or timezone component),
          // we treat this as a UTC midnight date. This is necessary because on the server, such dates are created as UTC midnight,
          // and this approach ensures correct behavior for equality operator filter comparisons (e.g., filtering for "2024-06-01" will
          // properly match a UTC midnight date created on the backend). For other date formats (those containing time and/or timezone information),
          // we parse the full time and timezone information from the string before converting to a UTC timestamp, ensuring filter comparisons
          // use the intended moment in time. See also: getDateForFilter for additional formats.
          // For 'gt' and 'lte' operators, we add 1 day to the date string to ensure that filtering for dates greater than a given date
          // excludes entries with that exact date (e.g., filtering for dates > 2025-11-10 should show dates from 2025-11-11 onwards).
          const parsedDate = isMatch(dateString, tableDateFormat.dateFormat)
            ? (() => {
                if (operator === 'gt' || operator === 'lte') {
                  // Add 1 day to the date string for 'gt' and 'lte' operators
                  const baseDate = new Date(dateString);
                  const adjustedDate = addDays(baseDate, 1);
                  const adjustedDateString = format(
                    adjustedDate,
                    tableDateFormat.dateFormat,
                  );
                  return new Date(adjustedDateString);
                }
                return new Date(dateString); // convert dateString in the short 'yyyy-MM-dd' format to UTC midnight date
              })()
            : getDateForFilter(dateString, ruleContext); // parse the date for other date-time formats including the time and timezone information

          return (
            parsedDate ??
            stumbling(
              'Invalid date; falling back to current date to satisfy types',
            )(new Date())
          );
        };
        (acc as any)[key] = [readableFilter[0], getDatePart().getTime()];

        return acc;
      }

      const filter = filterField.child
        ? [filterField.child, ...unformattedReadableFilter]
        : unformattedReadableFilter;

      (acc as any)[key] = filterField.array ? ['any', ...filter] : filter;

      return acc;
    }, {});

    cmdContext.updateState((draft) => {
      draft.advancedFilter = advancedFilterInitialState;
      draft.simpleFilterFieldsSubmitted = simpleFilterFields;
    });
    dispatch(setFilters, { filters, origin: 'simple' });

    cmdContext.onFiltersSubmit?.(cmdContext.getState().filters);
    dispatch(reloadTableData);
  };
